import { Pencil, Plus } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  FormField,
  Input,
  Modal,
  Select,
  type DataTableColumn,
} from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type AdminUser } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { AdminSkeleton, useAdminData } from './AdminLayout';

export function AdminUsersPage() {
  const t = useT();
  const { users, reload } = useAdminData();
  const notify = useNotify();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);

  const setActive = async (user: AdminUser, isActive: boolean) => {
    try {
      await api.admin.updateUser(user.id, { isActive });
      notify.success(
        t('admin.userToggled', {
          email: user.email,
          state: isActive ? t('admin.activated') : t('admin.deactivated'),
        }),
      );
      await reload();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    }
  };

  const columns: DataTableColumn<AdminUser>[] = [
    {
      key: 'email',
      header: t('auth.email'),
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-bold text-t0">{row.email}</p>
          {row.name && <p className="truncate text-[11.5px] text-t2">{row.name}</p>}
        </div>
      ),
    },
    {
      key: 'role',
      header: t('account.role'),
      render: (row) => (
        <Badge variant={row.role === 'ADMIN' ? 'accent' : 'neutral'}>
          {row.role === 'ADMIN' ? t('account.roleAdmin') : t('account.roleCustomer')}
        </Badge>
      ),
    },
    {
      key: 'state',
      header: t('admin.state'),
      hideBelow: 'md',
      render: (row) => (
        <Badge variant={row.isActive ? 'success' : 'danger'} dot>
          {row.isActive ? t('common.active') : t('admin.locked')}
        </Badge>
      ),
    },
    {
      key: 'totp',
      header: t('admin.twoFa'),
      hideBelow: 'lg',
      render: (row) => (
        <span className="text-t1">{row.totpEnabled ? t('common.active') : t('common.inactive')}</span>
      ),
    },
    {
      key: 'instances',
      header: t('nav.instances'),
      hideBelow: 'md',
      render: (row) => <span className="tabular-nums text-t1">{row.instanceCount}</span>,
    },
    {
      key: 'lastLogin',
      header: t('admin.lastLogin'),
      hideBelow: 'lg',
      render: (row) => <span className="text-[11.5px] text-t2">{formatRelative(row.lastLoginAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '175px',
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            title={t('admin.editUser')}
            aria-label={t('admin.editUser')}
            onClick={(event) => {
              event.stopPropagation();
              setEditTarget(row);
            }}
            className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-vela-sm)] text-t1 hover:bg-bg-3"
          >
            <Pencil size={14} />
          </button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              void setActive(row, !row.isActive);
            }}
          >
            {row.isActive ? t('admin.deactivate') : t('admin.activate')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AdminSkeleton>
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <p className="text-[15px] font-bold text-t0">{t('admin.users')}</p>
          <Button size="sm" icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            {t('admin.createUser')}
          </Button>
        </div>
        <DataTable columns={columns} data={users} rowKey={(row) => row.id} />
      </Card>

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => {
          setCreateOpen(false);
          notify.success(t('admin.userCreated'));
          await reload();
        }}
      />

      <EditUserDialog
        user={editTarget}
        onClose={() => setEditTarget(null)}
        onChanged={async () => {
          setEditTarget(null);
          await reload();
        }}
      />
    </AdminSkeleton>
  );
}

/**
 * Bearbeiten eines Kontos: Stammdaten, Zugriff und die beiden Notfallhebel
 * (2FA zuruecksetzen, Konto loeschen). Loeschen verlangt das Abtippen der
 * Adresse - das ist kein Klick, der versehentlich passiert.
 */
function EditUserDialog({
  user,
  onClose,
  onChanged,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const t = useT();
  const notify = useNotify();
  const { user: me } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'CUSTOMER'>('CUSTOMER');
  const [locale, setLocale] = useState<'de' | 'en'>('de');
  const [emailVerified, setEmailVerified] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmDelete, setConfirmDelete] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name ?? '');
    setEmail(user.email);
    setRole(user.role);
    setLocale(user.locale ?? 'de');
    setEmailVerified(user.emailVerified ?? true);
    setPassword('');
    setConfirmDelete('');
  }, [user]);

  if (!user) return null;
  const isSelf = me?.id === user.id;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.admin.updateUser(user.id, {
        name: name.trim() || null,
        email: email.trim().toLowerCase() !== user.email ? email.trim().toLowerCase() : undefined,
        role: isSelf ? undefined : role,
        locale,
        emailVerified,
        password: password.trim() || undefined,
      });
      notify.success(t('admin.userSaved', { email: user.email }));
      await onChanged();
    } catch (cause) {
      notify.fromError(cause, t('errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const sendVerification = async () => {
    setBusy(true);
    try {
      const result = await api.admin.sendVerification(user.id);
      notify.success(t('admin.sendVerificationDone', { to: result.to }));
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const resetTotp = async () => {
    setBusy(true);
    try {
      await api.admin.resetTotp(user.id);
      notify.success(t('admin.totpResetDone', { email: user.email }));
      await onChanged();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.admin.deleteUser(user.id);
      notify.success(t('admin.userDeleted', { email: user.email }));
      await onChanged();
    } catch (cause) {
      notify.fromError(cause, t('errors.deleteFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={t('admin.editUserTitle', { email: user.email })}>
      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('common.name')}>
            <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} />
          </FormField>
          <FormField label={t('auth.email')}>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </FormField>
          <FormField label={t('account.role')} hint={isSelf ? t('admin.roleSelfHint') : undefined}>
            <Select
              value={role}
              disabled={isSelf}
              onChange={(event) => setRole(event.target.value as 'ADMIN' | 'CUSTOMER')}
            >
              <option value="CUSTOMER">{t('account.roleCustomer')}</option>
              <option value="ADMIN">{t('account.roleAdmin')}</option>
            </Select>
          </FormField>
          <FormField label={t('nav.language')}>
            <Select value={locale} onChange={(event) => setLocale(event.target.value as 'de' | 'en')}>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </Select>
          </FormField>
        </div>

        <Checkbox
          checked={emailVerified}
          onChange={(event) => setEmailVerified(event.target.checked)}
          label={t('admin.emailVerifiedLabel')}
        />

        <FormField label={t('admin.newPassword')} hint={t('admin.newPasswordHint')}>
          <Input
            type="text"
            className="font-mono text-xs"
            minLength={10}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </FormField>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {t('common.save')}
          </Button>
        </div>
      </form>

      <div className="mt-5 space-y-3 border-t border-line pt-4">
        {!user.emailVerified && (
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[13px] font-semibold text-t0">{t('admin.sendVerificationTitle')}</p>
              <p className="text-[12px] text-t2">{t('admin.sendVerificationHint')}</p>
            </div>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void sendVerification()}>
              {t('admin.sendVerification')}
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[13px] font-semibold text-t0">{t('admin.totpResetTitle')}</p>
            <p className="text-[12px] text-t2">{t('admin.totpResetHint')}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !user.totpEnabled}
            onClick={() => void resetTotp()}
          >
            {t('admin.totpReset')}
          </Button>
        </div>

        {!isSelf && (
          <div className="rounded-[var(--radius-vela-md)] border border-bad/30 bg-bad-soft p-3">
            <p className="text-[13px] font-semibold text-t0">{t('admin.deleteUserTitle')}</p>
            <p className="mt-0.5 text-[12px] text-t1">
              {user.instanceCount > 0
                ? t('admin.deleteUserBlocked', { count: user.instanceCount })
                : t('admin.deleteUserHint', { email: user.email })}
            </p>
            {user.instanceCount === 0 && (
              <div className="mt-2 flex gap-2">
                <Input
                  className="font-mono text-xs"
                  placeholder={user.email}
                  value={confirmDelete}
                  onChange={(event) => setConfirmDelete(event.target.value)}
                />
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy || confirmDelete.trim().toLowerCase() !== user.email}
                  onClick={() => void remove()}
                >
                  {t('common.delete')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function CreateUserDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'CUSTOMER'>('CUSTOMER');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.admin.createUser({ email, password, role });
      await onCreated();
      setEmail('');
      setPassword('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Benutzer konnte nicht angelegt werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Benutzer anlegen">
      <form onSubmit={submit} className="space-y-4">
        <FormField label="E-Mail-Adresse" required>
          <Input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </FormField>
        <FormField label="Initialpasswort" required hint="Mindestens 10 Zeichen.">
          <Input
            type="text"
            className="font-mono text-xs"
            required
            minLength={10}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </FormField>
        <FormField label="Rolle">
          <Select value={role} onChange={(event) => setRole(event.target.value as 'ADMIN' | 'CUSTOMER')}>
            <option value="CUSTOMER">Kunde</option>
            <option value="ADMIN">Administrator</option>
          </Select>
        </FormField>
        {error && (
          <div className="rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-3 py-2 text-[13px] text-bad">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            Anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
